/**
 * A drawn plan of each spread's actual card positions.
 *
 * Replaces five PNG icons (~278 KB) that were generic mystical art and said
 * nothing about the layout being chosen. Everything is stroked in
 * `currentColor`, so the card's hover and selected states colour the diagram
 * with no second rule, and it stays sharp at any density. Each card is the
 * deck's own back in miniature, so the plan reads as the cards it deals.
 *
 * Positions are authored around an origin on a 28 × 38 step (card plus gap),
 * then each layout gets a viewBox fitted to its own bounds. Fitting means a
 * ten-card Celtic Cross and a single card both fill the plate instead of one
 * of them being lost in it; the floor stops small layouts blowing up.
 */

const W = 22   // card width
const H = 32   // card height
const R = 2.5  // corner radius
const PAD = 9
const MIN_W = 116
const MIN_H = 84

// [x, y, rotated?, accent?] — accent marks the position the spread turns on
const LAYOUTS = {
    'one-card': [
        [0, 0, false, true],
    ],
    'three-card': [
        [-28, 0], [0, 0, false, true], [28, 0],
    ],
    // The cross, its crossing card, and the staff of four up the right
    'celtic-cross': [
        [0, 0, false, true], [0, 0, true],
        [0, -38], [0, 38], [-34, 0], [34, 0],
        [72, -57], [72, -19], [72, 19], [72, 57],
    ],
    // Two facing columns, what passes between them, and where it leads
    relationship: [
        [-38, -38], [0, -38, false, true], [38, -38],
        [-38, 0], [0, 0], [38, 0],
        [0, 38],
    ],
    // A path climbing from where you are to where it goes
    career: [
        [-68, 52], [-34, 26], [0, 0, false, true], [34, -26], [68, -52],
    ],
}

const bounds = (cards) => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity

    for (const [x, y, rotated] of cards) {
        const w = rotated ? H : W
        const h = rotated ? W : H
        minX = Math.min(minX, x - w / 2)
        maxX = Math.max(maxX, x + w / 2)
        minY = Math.min(minY, y - h / 2)
        maxY = Math.max(maxY, y + h / 2)
    }

    const width = Math.max(maxX - minX + PAD * 2, MIN_W)
    const height = Math.max(maxY - minY + PAD * 2, MIN_H)
    const cx = (minX + maxX) / 2
    const cy = (minY + maxY) / 2

    return `${cx - width / 2} ${cy - height / 2} ${width} ${height}`
}

// The deck's back (see CardBack) cut down to what survives at thumbnail
// size: the double frame, one ring of the rosette, the eight-pointed star
// and its centre. Drawn around the card's own centre.
const STAR = 7.6

const MiniBack = () => (
    <>
        <rect
            className="spread-diagram__ground"
            x={-W / 2}
            y={-H / 2}
            width={W}
            height={H}
            rx={R}
        />
        <rect
            className="spread-diagram__line spread-diagram__line--faint"
            x={-W / 2 + 2.2}
            y={-H / 2 + 2.2}
            width={W - 4.4}
            height={H - 4.4}
            rx={R - 1.5}
        />
        <circle className="spread-diagram__line" r="6.4" />
        <rect
            className="spread-diagram__line spread-diagram__line--faint"
            x={-STAR / 2}
            y={-STAR / 2}
            width={STAR}
            height={STAR}
        />
        <rect
            className="spread-diagram__line spread-diagram__line--faint"
            x={-STAR / 2}
            y={-STAR / 2}
            width={STAR}
            height={STAR}
            transform="rotate(45)"
        />
        <circle className="spread-diagram__pip" r="1.3" />
    </>
)

const SpreadDiagram = ({ spreadId }) => {
    const cards = LAYOUTS[spreadId]
    if (!cards) return null

    return (
        <svg
            className="spread-diagram"
            viewBox={bounds(cards)}
            aria-hidden="true"
            focusable="false"
        >
            {cards.map(([x, y, rotated, accent], i) => (
                <g
                    key={i}
                    className={
                        accent
                            ? 'spread-diagram__card spread-diagram__card--accent'
                            : 'spread-diagram__card'
                    }
                    transform={`translate(${x} ${y})${rotated ? ' rotate(90)' : ''}`}
                >
                    <MiniBack />
                </g>
            ))}
        </svg>
    )
}

export default SpreadDiagram
